import styled from '@emotion/styled'
import {css} from '@emotion/react'
import {theme} from '../../theme'

// ─────────────────────────────────────────────
// Size styles
// ─────────────────────────────────────────────

const sizeStyles = ({size = 'md'}) => {
  switch (size) {
    case 'sm':
      return css`
        height: 32px;
        padding: 0 ${theme.space[2]};
        font-size: ${theme.fontSizes.sm};
      `

    case 'lg':
      return css`
        height: 46px;
        padding: 0 ${theme.space[4]};
        font-size: ${theme.fontSizes.lg};
      `

    case 'md':
    default:
      return css`
        height: 36px;
        padding: 0 ${theme.space[3]};
        font-size: ${theme.fontSizes.sm};
      `
  }
}

// ─────────────────────────────────────────────
// Variant styles
// ─────────────────────────────────────────────

const variantStyles = ({variant = 'default', hasError}) => {
  switch (variant) {
    case 'form':
      return css`
        color: #6a7181;
        background: #ffffff;
        // background: #f7f7f7;
        border: 1px solid ${hasError ? '#d92d20' : '#E5E7EB'};
        border-radius: 6px;

        &:hover:not(:disabled) {
          border-color: #c8ccd2;
        }

        &:focus {
          border-color: ${hasError ? '#d92d20' : '#7c5cff'};
          box-shadow: 0 0 0 3px
            ${hasError ? 'rgba(217, 45, 32, 0.12)' : 'rgba(124, 92, 255, 0.15)'};
        }
      `

    case 'pill':
      return css`
        background: ${theme.colors.bg.primary};
        border: none;
        border-radius: 999px;
        height: 37px;
        max-width: 343px;

        &:focus {
          box-shadow: 0 0 0 2px ${theme.colors.input.focusRing};
        }
      `

    case 'minimal':
      return css`
        background: transparent;
        border: none;
        border-bottom: 1px solid
          ${hasError ? '#d92d20' : theme.colors.border.primary};
        border-radius: 0;
      `

    case 'payment':
      return css`
        background: #ffffff;
        border: 1px solid ${hasError ? '#d92d20' : '#bfbfbf'};
        border-radius: 5px;
        height: 32px;
        padding: 8px;

        &:hover:not(:disabled) {
          border-color: ${hasError ? '#d92d20' : '#a0a0a0'};
        }

        &:focus {
          border-color: ${hasError ? '#d92d20' : '#bfbfbf'};
          box-shadow: none;
        }

        font-family: Open Sans;
        font-weight: 400;
        font-style: Regular;
        font-size: 12px;
        leading-trim: NONE;
        line-height: 100%;
        letter-spacing: 0%;
      `

    case 'default':
    default:
      return css`
        background: ${theme.colors.input.bg};
        border: 1px solid
          ${hasError
            ? theme.colors.input.borderError
            : theme.colors.input.border};
        border-radius: ${theme.radii.xs};

        &:hover:not(:disabled) {
          border-color: ${theme.colors.input.borderHover};
        }

        &:focus {
          border-color: ${hasError
            ? theme.colors.input.borderError
            : theme.colors.input.borderFocus};

          box-shadow: 0 0 0 3px
            ${hasError
              ? theme.colors.input.errorRing
              : theme.colors.input.focusRing};
        }
      `
  }
}

// ─────────────────────────────────────────────
// Wrapper
// ─────────────────────────────────────────────

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space[1]};
  width: 100%;
`

// ─────────────────────────────────────────────
// Label
// ─────────────────────────────────────────────

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const LabelIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  color: #6a7181;

  svg {
    width: 12px;
    height: 12px;
  }
`

export const LabelEl = styled.label`
  display: block;

  font-family: ${({variant}) =>
    variant === 'payment' ? "'Open Sans', Regular" : 'inherit'};

  font-size: ${({variant}) =>
    variant === 'form' || variant === 'payment' ? '12px' : theme.fontSizes.md};

  font-weight: ${({variant}) =>
    variant === 'payment' ? theme.fontWeights.bold : theme.fontWeights.medium};

  line-height: ${({variant}) => (variant === 'payment' ? '100%' : 'normal')};

  color: ${({variant}) =>
    variant === 'payment' ? '#000000' : theme.colors.text.primary};

  margin-bottom: ${({variant}) => (variant === 'payment' ? '0' : undefined)};
`
export const LabelReq = styled.span`
  display: block;

  font-size: ${({variant}) =>
    variant === 'form' ? '12px' : theme.fontSizes.md};

  font-weight: ${({variant}) =>
    variant === 'form' ? theme.fontWeights.medium : theme.fontWeights.medium};

  color: ${theme.colors.status.danger.text};
`

// ─────────────────────────────────────────────
// Input wrapper
// ─────────────────────────────────────────────

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

// ─────────────────────────────────────────────
// Input
// ─────────────────────────────────────────────

export const StyledInput = styled.input`
  width: 100%;
  background: ${theme.colors.input.bg};
  color: ${theme.colors.input.text};
  border: 1px solid
    ${({hasError}) =>
      hasError ? theme.colors.input.borderError : theme.colors.input.border};
  border-radius: ${theme.radii.md};
  outline: none;
  transition: border-color ${theme.transitions.normal},
    box-shadow ${theme.transitions.normal};

  color: ${theme.colors.input.text};

  font-size: ${({variant}) =>
    variant === 'form' ? '14px' : theme.fontSizes.sm};

  ${sizeStyles}

  ${variantStyles}

  padding-left: ${({hasLeftIcon}) =>
    hasLeftIcon ? theme.space[10] : undefined};

  padding-right: ${({hasRightIcon}) =>
    hasRightIcon ? theme.space[8] : undefined};

  &::placeholder {
    color: ${theme.colors.input.placeholder};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${theme.colors.bg.disabled};
  }
`

// ─────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────

export const IconSlot = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${theme.colors.text.primary};

  pointer-events: ${({clickable}) => (clickable ? 'auto' : 'none')};

  cursor: ${({clickable}) => (clickable ? 'pointer' : 'default')};

  font-size: 18px;

  ${({side}) =>
    side === 'left'
      ? css`
          left: ${theme.space[3]};
        `
      : css`
          right: ${theme.space[3]};
        `}
`
export const ActionSlot = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
  justify-content: center;

  color: ${theme.colors.text.primary};

  pointer-events: ${({clickable}) => (clickable ? 'auto' : 'none')};

  cursor: ${({clickable}) => (clickable ? 'pointer' : 'default')};

  font-size: 18px;

  ${({side}) =>
    side === 'left'
      ? css`
          left: ${theme.space[3]};
        `
      : css`
          right: ${theme.space[3]};
        `}
`

// ─────────────────────────────────────────────
// info text and Info Link
// ─────────────────────────────────────────────

export const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  // margin-top: 6px;
`

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`
export const StyledLinkIcon = styled.div`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${theme.palette.brand[600]};

  &::placeholder {
    color: ${theme.palette.brand[600]};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const InfoText = styled.span`
  display: block;

  font-size: ${({variant}) =>
    variant === 'form' ? '12px' : theme.fontSizes.md};

  font-weight: ${({variant}) =>
    variant === 'form' ? theme.fontWeights.medium : theme.fontWeights.medium};

  color: ${({variant}) =>
    variant === 'form' ? '#6A7181' : theme.colors.text.primary};
`

export const InfoLink = styled.a`
  font-size: 12px;
  text-decoration: underline;
  color: ${theme.palette.brand[600]};
`
// ─────────────────────────────────────────────
// Helper text
// ─────────────────────────────────────────────

export const HelperText = styled.p`
  margin: 0;

  font-size: ${theme.fontSizes.sm};

  color: ${({isError}) =>
    isError ? theme.colors.status.danger.text : theme.colors.text.muted};
`

// import styled from '@emotion/styled'
// import {css} from '@emotion/react'
// import {theme} from '../../theme' // ← direct import, no ThemeProvider needed

// // ─── Size map ─────────────────────────────────────────────────────────────────

// const sizeStyles = ({size = 'md'}) => {
//   switch (size) {
//     case 'sm':
//       return css`
//         height: 32px;
//         padding: 0 ${theme.space[2]};
//         font-size: ${theme.fontSizes.sm};
//       `
//     case 'lg':
//       return css`
//         height: 46px;
//         padding: 0 ${theme.space[4]};
//         font-size: ${theme.fontSizes.lg};
//       `
//     case 'md':
//     default:
//       return css`
//         height: 38px;
//         padding: 0 ${theme.space[3]};
//         font-size: ${theme.fontSizes.base};
//       `
//   }
// }

// // ─── Styled components ────────────────────────────────────────────────────────

// export const Wrapper = styled.div`
//   display: flex;
//   flex-direction: column;
//   gap: ${theme.space[1]};
//   width: 100%;
// `

// export const LabelEl = styled.label`
//   display: block;
//   font-size: ${theme.fontSizes.md};
//   font-weight: ${theme.fontWeights.medium};
//   color: ${theme.colors.text.primary};
// `

// export const InputWrapper = styled.div`
//   position: relative;
//   display: flex;
//   align-items: center;
// `

// export const StyledInput = styled.input`
//   width: 100%;
//   background: ${theme.colors.input.bg};
//   color: ${theme.colors.input.text};
//   border: 1px solid
//     ${({hasError}) =>
//       hasError ? theme.colors.input.borderError : theme.colors.input.border};
//   border-radius: ${theme.radii.xs};
//   outline: none;
//   transition: border-color ${theme.transitions.normal},
//     box-shadow ${theme.transitions.normal};

//   padding-left: ${({hasLeftIcon}) =>
//     hasLeftIcon ? theme.space[8] : undefined};
//   padding-right: ${({hasRightIcon}) =>
//     hasRightIcon ? theme.space[8] : undefined};

//   ${sizeStyles}

//   &::placeholder {
//     color: ${theme.colors.input.placeholder};
//   }

//   &:hover:not(:disabled) {
//     border-color: ${theme.colors.input.borderHover};
//   }

//   &:focus {
//     border-color: ${({hasError}) =>
//       hasError
//         ? theme.colors.input.borderError
//         : theme.colors.input.borderFocus};
//     box-shadow: 0 0 0 3px
//       ${({hasError}) =>
//         hasError ? theme.colors.input.errorRing : theme.colors.input.focusRing};
//   }

//   &:disabled {
//     opacity: 0.5;
//     cursor: not-allowed;
//     background: ${theme.colors.bg.secondary};
//   }
// `

// export const IconSlot = styled.span`
//   position: absolute;
//   top: 50%;
//   transform: translateY(-50%);
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   color: ${theme.colors.text.primary};
//   pointer-events: ${({clickable}) => (clickable ? 'auto' : 'none')};
//   cursor: ${({clickable}) => (clickable ? 'pointer' : 'default')};
//   font-size: 18px;

//   ${({side}) =>
//     side === 'left'
//       ? css`
//           left: ${theme.space[3]};
//         `
//       : css`
//           right: ${theme.space[3]};
//         `}
// `

// export const HelperText = styled.p`
//   font-size: ${theme.fontSizes.sm};
//   margin: 0;
//   color: ${({isError}) =>
//     isError ? theme.colors.status.danger.text : theme.colors.text.muted};
// `
